import { I18N_KEYS } from '@/language/keys';
import type {
  WorkflowCommunityPublishCustomFontReference,
  WorkflowCommunityPublishReadiness,
} from './useWorkflowCommunityPublishReadiness';
import type { CommunityPublishIneligibilityReason } from '@/utils/communityPublishingEligibility.utils';

type Translate = (key: string, options?: Record<string, string>) => string;

function formatFontFamilyKey(fontFamilyKey: string): string {
  return fontFamilyKey.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

function formatCustomFontReference(reference: WorkflowCommunityPublishCustomFontReference): string {
  const fontFamily = formatFontFamilyKey(reference.fontFamilyKey);
  return reference.nodeName ? `${fontFamily} in "${reference.nodeName}"` : fontFamily;
}

export function hasRuntimeCommunityPublishBlocker(readiness: WorkflowCommunityPublishReadiness): boolean {
  return readiness.blocker !== null && readiness.blocker !== 'not_public';
}

export function getCommunityPublishReadinessBlockerMessage(
  readiness: WorkflowCommunityPublishReadiness,
  t: Translate,
  orgDestination?: { name?: string },
): string | null {
  if (readiness.blocker === 'custom_fonts') {
    const fonts =
      readiness.customFontReferences.length > 0
        ? readiness.customFontReferences.map(formatCustomFontReference).join(', ')
        : readiness.customFontFamilyKeys.map(formatFontFamilyKey).join(', ');
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_TOOLTIP_CUSTOM_FONTS, { fonts });
  }

  if (readiness.blocker === 'figma_node') {
    if (orgDestination?.name) {
      return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ROW_TOOLTIP_FIGMA_NODE, {
        orgName: orgDestination.name,
      });
    }
    if (orgDestination) {
      return t(
        I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ROW_TOOLTIP_FIGMA_NODE_ORG_UNKNOWN,
      );
    }
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_TOOLTIP_FIGMA_NODE);
  }

  if (readiness.blocker === 'unverified_models') {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_TOOLTIP_UNVERIFIED_MODELS, {
      models: readiness.unverifiedModels.map((model) => model.displayName).join(', '),
    });
  }

  return null;
}

export function getCommunityPublishEligibilityBlockerMessage(
  reason: CommunityPublishIneligibilityReason | null,
  t: Translate,
): string | null {
  if (reason === 'banned') {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_TOOLTIP_BANNED);
  }

  if (reason === 'enterprise') {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_TOOLTIP_ENTERPRISE);
  }

  if (reason === 'org_member') {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_TOOLTIP_ORG_MEMBER);
  }

  return null;
}
